import InfiniteScroll from "react-infinite-scroll-component";
import { AccessLevel } from "@/api/williams/users";
import { ConfirmationDialog } from "@/components/dialogs";
import { Contractor } from "@/api/williams/contractors";
import { ExpandableCard, PinButton } from "../../components";
import { faChevronDown, faChevronUp, faEllipsisVertical } from "@fortawesome/free-solid-svg-icons";
import { faSquarePlus } from "@fortawesome/free-regular-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { getFullPath, routes } from "../../routes";
import { getStatusText } from "../lookup/status";
import { highlight } from "@/util";
import { Link, useNavigate } from "react-router-dom";
import { Project } from "@/api/project";
import { String } from "@/util";
import { TestEventFilters, TestEventListView, TestEventStatus } from "@/api/williams/testEvents";
import { useDeleteTestEvent, useDuplicateTestEvent, usePinTestEvent, useTestEventList } from "../hooks";
import { useModalDialog } from "@/hooks";
import { usePermissions } from "../../users/hooks";
import { useState } from "react";

export type TestEventsProps = {
  filters: Partial<TestEventFilters>;
  expanded?: boolean;
  allowExpand?: boolean;
  project?: Project;
  contractor?: Contractor;
};

export function TestEvents({ filters, project, contractor, ...props }: TestEventsProps) {
  const events = useTestEventList(filters);
  const permissions = usePermissions();

  const data = events.data?.pages.flatMap((page) => page.results) ?? [];
  const total = events.data?.pages[0]?.total ?? 0;
  const readOnly = permissions.data?.accessLevel === AccessLevel.GeneralUser;

  return (
    <ExpandableCard
      caption={`Test Events (${total})`}
      expanded={props.expanded}
      allowExpand={props.allowExpand}
      headerButtons={[
        <Link to={getFullPath(routes.testEvent.create)} state={{ project, contractor }} hidden={readOnly}>
          <FontAwesomeIcon icon={faSquarePlus} size="sm" title="Add Test Event" />
        </Link>,
      ]}
    >
      <InfiniteScroll
        dataLength={data.length}
        next={events.fetchNextPage}
        hasMore={Boolean(events.hasNextPage)}
        loader={<p>Loading...</p>}
      >
        {events.isLoading ? (
          <p>Loading...</p>
        ) : events.isError ? (
          <p>An unknown error occurred.</p>
        ) : events.isSuccess && data.length > 0 ? (
          <TestEventTable data={data} search={filters.searchString} />
        ) : filters.searchString ? (
          <p>Your search returned no results.</p>
        ) : (
          <p>No test events found.</p>
        )}
      </InfiniteScroll>
    </ExpandableCard>
  );
}

function TestEventTable({ data, search }: { data: TestEventListView[]; search?: string }) {
  return (
    <table className="table table-sm table-borderless mb-0">
      <thead>
        <tr>
          <th className="col col-md-2">Event ID</th>
          <th className="col-5 d-none d-md-table-cell">Project</th>
          <th className="col-5 d-none d-md-table-cell">Description</th>
          <th className="col-auto" />
        </tr>
      </thead>
      <tbody>
        {data.map((event) => (
          <TestEventRow key={event.id} event={event} search={search} />
        ))}
      </tbody>
    </table>
  );
}

function TestEventRow({ event, search }: { event: TestEventListView; search?: string }) {
  const [expanded, setExpanded] = useState<boolean>(false);

  const pin = usePinTestEvent();
  const permissions = usePermissions();

  return (
    <>
      <tr key={`${event.id}_${event.pinned}`}>
        <td>
          <Link to={getFullPath(routes.testEvent.open(event.id))} className="link link-primary">
            {highlight(event.number, search)}
          </Link>
        </td>
        <td className="d-none d-md-table-cell">
          <Link to={getFullPath(routes.project.open(event.projectId))} className="link link-primary">
            {highlight(String.truncate(event.projectNumber, 50), search)}
          </Link>
        </td>
        <td className="d-none d-md-table-cell">{highlight(String.truncate(event.description, 50), search)}</td>
        <td className="text-end text-nowrap">
          <FontAwesomeIcon
            role="button"
            icon={expanded ? faChevronUp : faChevronDown}
            size="sm"
            className="me-1"
            title="Details"
            onClick={() => setExpanded((prev) => !prev)}
            fixedWidth={true}
          />
          <PinButton pinned={event.pinned} onChange={() => pin.mutate(event.id)} />
          {permissions.data?.accessLevel !== AccessLevel.GeneralUser && (
            <>
              <FontAwesomeIcon
                type="button"
                icon={faEllipsisVertical}
                size="sm"
                className="dropdown-toggle"
                title="More Options"
                data-bs-toggle="dropdown"
                fixedWidth={true}
              />
              <DropdownMenu event={event} />
            </>
          )}
        </td>
      </tr>
      {expanded && (
        <tr>
          <td className="d-none d-md-table-cell" colSpan={4}>
            <ExpandedDetails event={event} search={search} />
          </td>
          <td className="d-table-cell d-md-none" colSpan={2}>
            <ExpandedDetails event={event} search={search} />
          </td>
        </tr>
      )}
    </>
  );
}

function ExpandedDetails({ event, search }: { event: TestEventListView; search?: string }) {
  return (
    <div className="ms-1">
      <div className="bg-light p-1">Test Event Details</div>
      <div className="p-1">
        <dl>
          <dt className="d-block d-md-none">Project</dt>
          <dd className="d-block d-md-none">
            <Link to={getFullPath(routes.project.open(event.projectId))} className="link link-primary">
              {highlight(event.projectNumber, search)}
            </Link>
          </dd>
          <dt>Status</dt>
          <dd>{getStatusText(event.status)}</dd>
          <dt>Description</dt>
          <dd>{highlight(event.description, search)}</dd>
          <dt>Owner</dt>
          <dd>{event.owner}</dd>
          <dt>Welding/NDE Specialist</dt>
          <dd>{event.weldingSpecialist}</dd>
        </dl>
      </div>
    </div>
  );
}

export function DropdownMenu({ event }: { event: TestEventListView }) {
  const dialog = useModalDialog();
  const deleteEvent = useDeleteTestEvent(false);
  const permissions = usePermissions();
  const duplicate = useDuplicateTestEvent();
  const navigate = useNavigate();

  const accessLevel = permissions.data?.accessLevel ?? AccessLevel.GeneralUser;

  function viewTestResults() {
    navigate(getFullPath(routes.testEvent.results(event.id)));
  }

  function handleDelete() {
    dialog.show(ConfirmationDialog, {
      caption: "Delete Test Event",
      message: `Are you sure you want to delete test event ${event.number}?`,
      onAccept: () => {
        deleteEvent.mutate(event.id);
      },
    });
  }

  return (
    <ul className="dropdown-menu">
      {accessLevel >= AccessLevel.AIWeldingSME && (
        <li className="dropdown-item cursor-pointer" onClick={handleDelete}>
          Delete
        </li>
      )}
      <li className="dropdown-item cursor-pointer" onClick={() => duplicate(event.id)}>
        Create Duplicate
      </li>
      {event.status >= TestEventStatus.PartialTestResults && (
        <li className="dropdown-item cursor-pointer" onClick={viewTestResults}>
          View Test Results
        </li>
      )}
    </ul>
  );
}
